import React from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Row, Col, Tag, List, Avatar, Statistic, Skeleton } from 'antd';
import { TrophyOutlined, StarOutlined, RiseOutlined } from '@ant-design/icons';
import { useGetReputationQuery, useGetProfileQuery } from './profileApi';

const { Title, Text } = Typography;

function Panel({ title, icon, children, style }) {
  return (
    <div
      style={{
        borderRadius: 12,
        border: '1px solid var(--df-border)',
        background: 'var(--df-surface-elevated)',
        boxShadow: 'var(--df-shadow)',
        marginBottom: 20,
        ...style,
      }}
    >
      {title && (
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--df-border-subtle)',
            fontFamily: 'var(--df-font-display)',
            fontWeight: 700,
            fontSize: 15,
            color: 'var(--df-text)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {icon}
          {title}
        </div>
      )}
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

export default function ProfilePage() {
  const { publicId } = useParams();
  const { data: ownProfile } = useGetProfileQuery(undefined, { skip: !!publicId });
  const targetId = publicId || ownProfile?.public_id;
  const { data: rep, isLoading } = useGetReputationQuery(targetId, { skip: !targetId });

  if (isLoading) return <Skeleton active avatar paragraph={{ rows: 6 }} />;
  if (!rep) {
    return (
      <Text style={{ color: 'var(--df-text-secondary)' }}>User not found</Text>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <Panel>
        <Row align="middle" gutter={24}>
          <Col>
            <Avatar
              size={80}
              style={{
                background: 'linear-gradient(145deg, var(--df-primary) 0%, var(--df-primary-muted) 100%)',
                fontSize: 32,
                fontWeight: 700,
                color: '#fff',
                fontFamily: 'var(--df-font-display)',
              }}
            >
              {rep.displayName?.[0] || 'U'}
            </Avatar>
          </Col>
          <Col flex="auto">
            <Title
              level={3}
              style={{ margin: 0, color: 'var(--df-text)', fontFamily: 'var(--df-font-display)' }}
            >
              {rep.displayName}
            </Title>
            <Text style={{ fontSize: 14, display: 'block', marginTop: 4, color: 'var(--df-text-secondary)' }}>
              {rep.bio || 'No bio yet'}
            </Text>
            <div style={{ marginTop: 14 }}>
              <Statistic
                title={(
                  <Text style={{ fontSize: 12, fontWeight: 600, color: 'var(--df-text-secondary)' }}>
                    Reputation
                  </Text>
                )}
                value={rep.reputationScore || 0}
                prefix={<StarOutlined style={{ color: 'var(--df-accent)' }} />}
                valueStyle={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: 'var(--df-text)',
                  fontFamily: 'var(--df-font-display)',
                  padding: 0,
                }}
              />
            </div>
          </Col>
        </Row>
      </Panel>

      {rep.badges && rep.badges.length > 0 && (
        <Panel title="Badges" icon={<TrophyOutlined style={{ color: 'var(--df-accent)' }} />}>
          <Row gutter={[12, 12]}>
            {rep.badges.map((b, i) => (
              <Col key={i}>
                <Tag
                  style={{
                    borderRadius: 8,
                    border: '1px solid var(--df-border)',
                    background: 'rgba(232, 93, 4, 0.08)',
                    color: 'var(--df-primary)',
                    padding: '6px 14px',
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <TrophyOutlined style={{ color: 'var(--df-accent)' }} />
                  {b.badge} {b.tier && `(${b.tier})`}
                </Tag>
              </Col>
            ))}
          </Row>
        </Panel>
      )}

      <Panel title="Recent activity" icon={<RiseOutlined style={{ color: 'var(--df-primary)' }} />}>
        <List
          dataSource={rep.events || []}
          locale={{ emptyText: 'No activity yet' }}
          renderItem={(event) => (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 0',
                borderBottom: '1px solid var(--df-border-subtle)',
              }}
            >
              <Avatar
                size={36}
                style={{
                  background: 'rgba(11, 58, 92, 0.08)',
                  color: 'var(--df-primary)',
                  flexShrink: 0,
                }}
              >
                <RiseOutlined />
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: 13, fontWeight: 600, color: 'var(--df-text)', display: 'block' }}>
                  {event.action.replace(/_/g, ' ')}
                </Text>
                <Text style={{ fontSize: 12, color: 'var(--df-text-muted)' }}>
                  +{event.points} points · {event.createdAt ? new Date(event.createdAt).toLocaleDateString() : ''}
                </Text>
              </div>
            </div>
          )}
        />
      </Panel>
    </div>
  );
}
